import { makeAutoObservable, reaction, toJS } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import {
  StoreStatus,
  CallbackDataParams,
  InitializeParams,
} from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { editionService } from "../services/editionService";
import {
  EditionValuesInit,
  getEditionUpdateVariables,
  getEditionValues,
  EditionValuesType,
  EditionErrorsType,
  editionDisplayId,
} from "./EditionCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { Edition, SortOrder } from "../../../api/graphql";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getEditionTypeValues,
  EditionTypeValuesType,
  editionTypeDisplayId,
} from "../../editionType/store/EditionTypeCommonStore";
import { isNil } from "../../../common/utils";
import {
  getMusicalWorkValues,
  MusicalWorkValuesType,
  musicalWorkDisplayId,
} from "../../musicalWork/store/MusicalWorkCommonStore";
import { UserAttachmentEditionUpdateStore } from "../../userAttachmentEdition/store";
import { GridChangeSet } from "../../common/types";
import { EditionContentType } from "../../../api/graphql";
import { CrudRows } from "../../common/types";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { editionTypeService } from "../../editionType/services/editionTypeService";
import { musicalWorkService } from "../../musicalWork/services/musicalWorkService";
import { FileEditorStore } from "../../../common/stores/FileEditorStore";
import { downloadWithAuth } from "../../../common/utils/downloadWithAuth";
// import { extractAndMergePdfsFromZip } from "../../../common/utils/extractAndMergePdfsFromZip";
import { FileToDownload } from "../../../common/utils/downloadAllFilesInFolder";

type AsyncFunction = () => Promise<any>;

export class EditionUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: EditionErrorsType = {};
  service = editionService;
  editionInstance: Edition | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    editionWorkflow: { open: true, errors: [] },
    signatureStatus: { open: true, errors: [] },
    editionData: { open: true, errors: [] },
    contracts: { open: true, errors: [] },
    editionContracts: { open: true, errors: [] },
    editionDocumentation: { open: true, errors: [] },
    editionUserAttachments: { open: true, errors: [] },
    systemDates: { open: false, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  // Se guardó, pero el formulario no se pudo poner al día (la relectura falló, o
  // el formulario cambió mientras se guardaba): Guardar queda deshabilitado hasta
  // recargar (OutdatedAfterSaveNotice), así nunca se guarda desde un estado viejo.
  savedButOutdated: boolean = false;
  // En ese estado, lo que se escribe cuenta como cambio pendiente: la foto del
  // formulario cubre los campos; este vigía, también las grillas y los archivos.
  private editedWhileOutdated: boolean = false;
  private stopOutdatedWatch?: () => void;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = EditionValuesInit();
  documentationStateTransitionProcessing: boolean = false;
  nameError: string | undefined;
  editionTypeError: string | undefined;
  editionTypeService = editionTypeService;
  editionTypeSearchValue: string = "";
  editionTypeOptions: EditionTypeValuesType[] = [];
  contentTypeError: string | undefined;
  publicationLinkError: string | undefined;
  priceError: string | undefined;
  worksError: string | undefined;
  worksService = musicalWorkService;
  worksSearchValue: string = "";
  worksOptions: MusicalWorkValuesType[] = [];
  notesError: string | undefined;
  contractObservationError: string | undefined;
  documentationError: string | undefined;
  documentationVisibleColumns: { name: string; title: string }[];
  documentationColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
      multi?: boolean;
      onChange?: (value: string) => void;
      onSelect?: (value: any) => void;
      getOptions?: () => { displayId: string; id: string }[];
      choices?: {
        min: number;
        max: number;
        unitSingular?: string;
        unitPlural?: string;
        nullText?: string;
      };
    }
  >;
  associatedDocumentsError: string | undefined;
  associatedDocumentsVisibleColumns: { name: string; title: string }[];
  associatedDocumentsColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
      multi?: boolean;
      onChange?: (value: string) => void;
      onSelect?: (value: any) => void;
      getOptions?: () => { displayId: string; id: string }[];
      choices?: {
        min: number;
        max: number;
        unitSingular?: string;
        unitPlural?: string;
        nullText?: string;
      };
    }
  >;
  userAttachmentsError: string | undefined;
  userAttachmentsVisibleColumns: { name: string; title: string }[];
  userAttachmentsColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
      multi?: boolean;
      onChange?: (value: string) => void;
      onSelect?: (value: any) => void;
      getOptions?: () => { displayId: string; id: string }[];
      choices?: {
        min: number;
        max: number;
        unitSingular?: string;
        unitPlural?: string;
        nullText?: string;
      };
    }
  >;
  userAttachmentsUpdateStore: UserAttachmentEditionUpdateStore;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<EditionValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  fileEditorStore: FileEditorStore;

  // Estado de firmas electrónicas
  signatureStatuses: Array<{
    editionDocumentationId: string;
    contractNumber: string;
    contractName: string;
    documentStatus: string | null;
    signaturePhase: string | null;
    recipients: Array<{
      name: string;
      email: string;
      role: string;
      order: number | null;
      status: string;
      signedAt: string | null;
    }>;
  }> = [];

  // Estado para el visor de PDF
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";
  pdfViewerLoading: boolean = false;
  pdfViewerWarning: string = "";

  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.documentationVisibleColumns = [
      {
        name: "finalDocumentPdf",
        title: this.langStore.EditionDocumentationFinalDocumentPdfLabel,
      },
      {
        name: "documentName",
        title: this.langStore.EditionDocumentationDocumentNameLabel,
      },
    ];
    this.documentationColumnTypes = {
      finalDocumentPdf: {
        fieldType: "File" as const,
        nullable: true,
        readOnly: false,
      },
      documentName: {
        fieldType: "String" as const,
        nullable: true,
        readOnly: true,
      },
    };
    this.associatedDocumentsVisibleColumns = [
      {
        name: "file",
        title: this.langStore.AssociatedDocumentEditionFileLabel,
      },
    ];
    this.associatedDocumentsColumnTypes = {
      file: { fieldType: "File" as const, nullable: false, readOnly: false },
    };
    this.userAttachmentsVisibleColumns = [
      { name: "file", title: this.langStore.UserAttachmentEditionFileLabel },
    ];
    this.userAttachmentsColumnTypes = {
      file: { fieldType: "File" as const, nullable: false, readOnly: false },
    };
    this.userAttachmentsUpdateStore = new UserAttachmentEditionUpdateStore(
      rootStore,
      "userAttachments_" + this.storeId
    );
    this.fileEditorStore = new FileEditorStore(rootStore);
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<EditionValuesType>);
      }
    });
    makeAutoObservable(this, {
      originalState: false,
      rootStore: false,
      langStore: false,
      fileEditorStore: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    // Primero, lejos de lo que un proyecto agrega después del bloque de stopReaction.
    this.stopOutdatedWatch?.();
    if (this.stopReaction) {
      this.stopReaction();
    }
    if (this.fileEditorStore) {
      this.fileEditorStore.destroy();
    }

    if (this.userAttachmentsUpdateStore) {
      this.userAttachmentsUpdateStore.destroy();
    }
  }

  get documentationColumns() {
    if (this.entityValues.showDocumentation.filter((x: any)=>x.finalDocumentPdf).length > 0) {
      return [{
        name: "finalDocumentPdf",
        title: this.langStore.EditionDocumentationFinalDocumentPdfLabel,
      }];
    } else {
      return [{
        name: "contractNumber",
        title: this.langStore.EditionDocumentationContractNumberLabel,
      }];
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<EditionValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadEditionById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    try {
      this.editionInstance = yield this.service.getOne(
        id,
        validToken,
        this.rootStore.activityStore
      );
    } catch (error) {
      // Convergencia SIN eventos: si la edición ya no existe, lo más probable
      // es que el circuito de firmas completó y el snapshot la movió a
      // SignedEdition (el server la BORRA). Con SSE caído el cliente nunca se
      // entera — acá lo detecta por sí mismo y navega. Si no hay SignedEdition
      // con este origen, el error original sigue su curso.
      const navigated = yield this.navigateToSignedEditionIfMoved(id);
      if (navigated) return;
      throw error;
    }
    if (this.editionInstance) {
      // Lo que se ve es lo que hay en el server: el formulario está al día.
      this.becomeCurrent();
      this.entityValues = getEditionValues(
        this.editionInstance,
        this.langStore
      );
      this.saveOriginalState();
      yield this.loadSignatureStatuses(id);
      this.scheduleSignaturePolling(id);
    } else {
      yield this.navigateToSignedEditionIfMoved(id);
    }
  }

  /**
   * Busca la SignedEdition originada en esta edición (snapshot post-firma) y
   * navega a su vista. Devuelve true si navegó.
   */
  *navigateToSignedEditionIfMoved(editionId: string): any {
    try {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.signedEditionListWithOffset({
        where: { originalId_: { equals: editionId } } as any,
        take: 1,
        offset: 0,
      });
      const signedEdition = result.signedEditionListWithOffset?.[0];
      if (signedEdition?.id) {
        this.rootStore.showSnackbar(
          "La documentación fue firmada: la edición pasó a Ediciones Firmadas.",
          "success"
        );
        this.rootStore.router.goTo(
          `/signedEdition/view/${signedEdition.id}`,
          undefined,
          undefined,
          undefined,
          "Edición Firmada"
        );
        return true;
      }
    } catch {
      // best-effort: sin permiso o sin datos, dejar que el flujo original siga
    }
    return false;
  }

  /**
   * Red de seguridad ante SSE caído: mientras haya firmas pendientes, re-carga
   * el estado cada 60s. Frugal: solo corre con el form de ESTA edición abierto
   * y solo en los estados de firma; se corta solo al salir de ellos.
   */
  private signaturePollTimer: ReturnType<typeof setTimeout> | null = null;

  private scheduleSignaturePolling(editionId: string) {
    if (this.signaturePollTimer) {
      clearTimeout(this.signaturePollTimer);
      this.signaturePollTimer = null;
    }
    const state = this.entityValues?.showDocumentationState?.id || "";
    if (!["sentForSignature", "esperandoFirmaDjct"].includes(state)) return;

    this.signaturePollTimer = setTimeout(() => {
      this.signaturePollTimer = null;
      const stillOpen = window.location.pathname.includes(
        `/edition/update/${editionId}`
      );
      if (!stillOpen) return;
      // loadEditionById re-agenda el próximo tick (o navega si la edición
      // ya se movió a SignedEdition).
      (this as any).loadEditionById(editionId);
    }, 60000);
  }

  *loadSignatureStatuses(editionId: string): any {
    try {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore);
      const result = yield api.editionSignatureStatus({ editionId });
      this.signatureStatuses = result.editionSignatureStatus || [];
    } catch (error) {
      this.signatureStatuses = [];
    }
  }

  *resendSignatureEmail(editionDocumentationId: string, recipientEmail: string): any {
    const api = yield* getApi(this.rootStore.authStore, this.rootStore.activityStore);
    try {
      yield api.resendSignatureEmail({ editionDocumentationId, recipientEmail });
    } finally {
      // Refrescar SIEMPRE (también ante error): un fallo típico es
      // SIGNER_ALREADY_SIGNED con el panel desactualizado por SSE caído — la
      // recarga lo corrige, y si la edición ya se movió a SignedEdition,
      // loadEditionById navega solo. El try interno evita que un fallo del
      // refresh enmascare el error original del reenvío.
      const editionId = this.editionInstance?.id;
      if (editionId) {
        try {
          yield this.loadEditionById(editionId);
        } catch {
          // best-effort
        }
      }
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda, y con el formulario guardado pero desactualizado.
  get saveDisabled() {
    return this.isProcessing || this.savedButOutdated;
  }

  /**
   * Vuelve a leer el registro: la acción del aviso de guardado pero desactualizado.
   * Si la lectura sale bien el formulario queda al día y Guardar se vuelve a
   * habilitar; si no, el error queda a la vista y el aviso sigue.
   */
  *reloadEdition(): any {
    if (this.isProcessing) return;
    if (this.pendingChanges) {
      // Lo escrito después de quedar desactualizado se descartaría: se pregunta,
      // como al salir.
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.reloadWithoutSaving,
        this.executeReloadAction.bind(this)
      );
      return;
    }
    yield this.executeReloadAction();
  }

  *executeReloadAction(): any {
    yield this.process(() => this.loadEditionById(this.entityValues.id));
  }

  /**
   * Queda guardado pero desactualizado. Se toma la foto del formulario: lo editado
   * antes de guardar ya se guardó y no cuenta; lo que se escriba desde ahora cuenta
   * como cambio pendiente, y Cancelar, la navegación y Recargar avisan antes de
   * descartarlo. Para lo que la foto no cubre (las grillas y los archivos), el
   * vigía lo marca.
   */
  private becomeOutdated() {
    this.savedButOutdated = true;
    this.resetPendingChanges();
    this.saveOriginalState();
    this.editedWhileOutdated = false;
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = reaction(
      () => ({ ...toJS(this.entityValues), version_: undefined }),
      () => {
        this.editedWhileOutdated = true;
      },
      { equals: (a, b) => _.isEqual(a, b) }
    );
  }

  /** El formulario está al día (se volvió a leer el registro): se sale del estado. */
  private becomeCurrent() {
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = undefined;
    this.editedWhileOutdated = false;
    this.savedButOutdated = false;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return editionDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.editionInstance) {
      return editionDisplayId(this.editionInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EditionSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  viewFormWorks(id: string) {
    if (id) {
      this.rootStore.openOrPushOverlay("musicalWork", id, "view");
    }
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "editionData",
        label: this.rootStore.langStore.groupEditionEditionDataLabel,
        errors: this.getCardErrors("editionData"),
      },
      {
        id: "contracts",
        label: this.rootStore.langStore.groupEditionContractsLabel,
        errors: this.getCardErrors("contracts"),
      },
      {
        id: "editionContracts",
        label: this.rootStore.langStore.groupEditionEditionContractsLabel,
        errors: this.getCardErrors("editionContracts"),
      },
      {
        id: "editionDocumentation",
        label: this.rootStore.langStore.groupEditionEditionDocumentationLabel,
        errors: this.getCardErrors("editionDocumentation"),
      },
      {
        id: "editionUserAttachments",
        label: this.rootStore.langStore.groupEditionEditionUserAttachmentsLabel,
        errors: this.getCardErrors("editionUserAttachments"),
      },
      {
        id: "systemDates",
        label: this.rootStore.langStore.groupEditionSystemDatesLabel,
        errors: this.getCardErrors("systemDates"),
      },
    ];
  }

  *returnedData(data: any): any {
    const { callerData, returnData } = data;
    if (
      callerData &&
      returnData &&
      !returnData.cancelled &&
      (returnData.entity || returnData.resultId)
    ) {
      // Delegate to subentity UpdateStore if callerData.entityName matches a subentity
      if (callerData?.entityName === "UserAttachmentEdition") {
        this.userAttachmentsUpdateStore.isWaitingForCallback = false;
        this.userAttachmentsUpdateStore.callbackReturnPath = null;
        yield this.userAttachmentsUpdateStore.returnedData(data);
        return;
      }

      if (callerData?.fieldName === "editionType") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            editionTypeDisplayId(returnData.entity, this.langStore);
          this.entityValues.showEditionType = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.editionTypeOne({ id: returnData.resultId });
          const editionType = result.editionTypeOne;
          this.entityValues.showEditionType = {
            displayId: editionType.displayId,
            id: editionType.id,
          };
          this.setEditionTypeOptions([
            getEditionTypeValues(editionType, this.langStore),
          ]);
        }
      } else if (callerData?.fieldName === "works") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            musicalWorkDisplayId(returnData.entity, this.langStore);
          this.entityValues.showWorks.push({
            id: returnData.entity.id,
            displayId,
          });
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore);
          const result = yield api.musicalWorkOne({ id: returnData.resultId });
          const musicalWork = result.musicalWorkOne;
          this.entityValues.showWorks.push({
            displayId: musicalWork.displayId,
            id: musicalWork.id,
          });
          this.setWorksOptions([
            getMusicalWorkValues(musicalWork, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  *editionDocumentationWorkflow_GenerateDocumentation(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_GenerateDocumentation(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_DeleteAndRestartFromSignature(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_DeleteAndRestartFromSignature(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_CancelGeneration(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_CancelGeneration(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_DeleteAndRestartFromGenerated(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_DeleteAndRestartFromGenerated(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_DeleteAndRestartFromFailed(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_DeleteAndRestartFromFailed(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_RetryGenerateDocumentation(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_RetryGenerateDocumentation(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_SendForSignature(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_SendForSignature(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_CancelSending(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_CancelSending(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_RetrySendForSignature(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_RetrySendForSignature(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_BackToDocsFromSignatureFailed(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_BackToDocsFromSignatureFailed(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_BorrarDocumentacionFromFallo(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_BorrarDocumentacionFromFallo(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_ReintentarCierreFirmas(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_ReintentarCierreFirmas(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_BorrarDocumentacionDesdeEsperaDjct(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_BorrarDocumentacionDesdeEsperaDjct(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *editionDocumentationWorkflow_BorrarDocumentacionDesdeFalloEjemplar(): any {
    try {
      const validToken = yield tokenManager.getValidAccessToken();
      if (!validToken) {
        throw new Error("SESSION_EXPIRED");
      }
      this.rootStore.authStore.accessToken = validToken;
      const result =
        yield this.service.editionDocumentationWorkflow_BorrarDocumentacionDesdeFalloEjemplar(
          this.entityValues.id,
          this.entityValues.version_,
          "Edition",
          validToken,
          this.rootStore.activityStore
        );
      if (result && result.instanceId) {
        this.entityValues.version_ = result.instanceVersion;
        this.entityValues.documentationState_transitions_ =
          result.nextTransitions || [];
        yield this.loadEditionById(result.instanceId);
      }
    } catch (error) {
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *onDocumentationStateTransition(transitionName: string) {
    if (this.pendingChanges) {
      this.showErrorDialog(
        undefined,
        this.langStore.cannotProceedWithChanges,
        this.langStore.saveChangesFirst
      );
      return;
    }
    this.documentationStateTransitionProcessing = true;
    try {
      const transitionLabel =
        this.langStore.EditionDocumentationStateTransitionLabels[
          transitionName
        ];
      yield this.executeDocumentationStateTransition(transitionName);
      if (!this.errorDialogOpened) {
        const newStateId = this.entityValues.showDocumentationState?.id || "";
        const stateLabel =
          this.langStore.EditionDocumentationStateStateLabels[newStateId] || "";
        const message = `${transitionLabel}. ${this.langStore.workflowNewStatePrefix}: ${stateLabel}`;
        this.rootStore.showSnackbar(message, "success");
      }
    } finally {
      this.documentationStateTransitionProcessing = false;
    }
  }

  *executeDocumentationStateTransition(transitionName: string) {
    if (transitionName === "generateDocumentation") {
      yield this.editionDocumentationWorkflow_GenerateDocumentation();
    }
    if (transitionName === "deleteAndRestartFromSignature") {
      yield this.editionDocumentationWorkflow_DeleteAndRestartFromSignature();
    }
    if (transitionName === "cancelGeneration") {
      yield this.editionDocumentationWorkflow_CancelGeneration();
    }
    if (transitionName === "deleteAndRestartFromGenerated") {
      yield this.editionDocumentationWorkflow_DeleteAndRestartFromGenerated();
    }
    if (transitionName === "deleteAndRestartFromFailed") {
      yield this.editionDocumentationWorkflow_DeleteAndRestartFromFailed();
    }
    if (transitionName === "retryGenerateDocumentation") {
      yield this.editionDocumentationWorkflow_RetryGenerateDocumentation();
    }
    if (transitionName === "sendForSignature") {
      yield this.editionDocumentationWorkflow_SendForSignature();
    }
    if (transitionName === "cancelSending") {
      yield this.editionDocumentationWorkflow_CancelSending();
    }
    if (transitionName === "retrySendForSignature") {
      yield this.editionDocumentationWorkflow_RetrySendForSignature();
    }
    if (transitionName === "backToDocsFromSignatureFailed") {
      yield this.editionDocumentationWorkflow_BackToDocsFromSignatureFailed();
    }
    if (transitionName === "borrarDocumentacionFromFallo") {
      yield this.editionDocumentationWorkflow_BorrarDocumentacionFromFallo();
    }
    if (transitionName === "reintentarCierreFirmas") {
      yield this.editionDocumentationWorkflow_ReintentarCierreFirmas();
    }
    if (transitionName === "borrarDocumentacionDesdeEsperaDjct") {
      yield this.editionDocumentationWorkflow_BorrarDocumentacionDesdeEsperaDjct();
    }
    if (transitionName === "borrarDocumentacionDesdeFalloEjemplar") {
      yield this.editionDocumentationWorkflow_BorrarDocumentacionDesdeFalloEjemplar();
    }
  }

  get documentationStateTransitions() {
    const hasESignatureProvider =
      !!this.rootStore.authStore.me?.company?.extra?.eSignatureProvider;
    return (this.entityValues.documentationState_transitions_ || [])
      .filter((x) => !(x === "markAsFullySigned" && hasESignatureProvider))
      .map((x) => {
        return {
          id: x,
          label: this.langStore.EditionDocumentationStateTransitionLabels[x],
        };
      });
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  setEditionTypeOptions(values: EditionTypeValuesType[]) {
    this.editionTypeOptions = values;
  }

  *loadEditionTypeValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("EditionType");
    const allOptions = cache.getOptions(
      "EditionType"
    ) as EditionTypeValuesType[];

    const searchValue = this.editionTypeSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) => item.displayId.toLowerCase().includes(searchValue))
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setEditionTypeOptions(filtered);
  }

  getEditionTypeOptions() {
    return this.editionTypeOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setEditionTypeValue(value: string) {
    this.editionTypeSearchValue = value;
    yield this.loadEditionTypeValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedEditionType(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewEditionType(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "editionType"
      );
    } else {
      if (value) {
        this.entityValues.showEditionType = value;
        this.entityValues.__editionType = value.displayId;
      }
    }
  }

  getContentTypeValue(): EditionContentType {
    return this.entityValues.contentType;
  }

  setContentTypeValue(value: EditionContentType) {
    this.entityValues.contentType = value;
  }

  getContentTypeOptions() {
    return this.langStore.EditionContentTypeOptions;
  }

  getPublicationLinkValue(): string {
    return this.entityValues.publicationLink;
  }

  setPublicationLinkValue(value: string) {
    this.entityValues.publicationLink = value;
  }

  getPriceValue(): number {
    return this.entityValues.price;
  }

  setPriceValue(value: number) {
    this.entityValues.price = value;
  }

  setWorksOptions(values: MusicalWorkValuesType[]) {
    this.worksOptions = values;
  }

  *loadWorksValues(): any {
    let where: any = null;
    if (this.worksSearchValue) {
      const searchFilter = {
        title: { contains: this.worksSearchValue, mode: "insensitive" },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore);
    const result = yield api.musicalWorkListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ lastUpdatedAt_: SortOrder.Desc }],
    });
    this.setWorksOptions(
      (result.musicalWorkListWithOffset || []).map((x: any) =>
        getMusicalWorkValues(x, this.langStore)
      )
    );
  }

  getWorksOptions() {
    return this.worksOptions.map((x: any) => ({
      ...x,
      displayId: resolveCurrentLang(x.displayId, this.langStore),
    }));
  }

  *setWorksValue(value: string) {
    this.worksSearchValue = value;
    yield this.loadWorksValues();
  }

  setSelectedWorks(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewMusicalWork(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "works"
      );
    } else {
      this.entityValues.showWorks = values;
      this.entityValues.__works = values.map((v) => v.displayId);
    }
  }

  getNotesValue(): string {
    return this.entityValues.notes;
  }

  setNotesValue(value: string) {
    this.entityValues.notes = value;
  }

  getContractObservationValue(): string {
    return this.entityValues.contractObservation;
  }

  setContractObservationValue(value: string) {
    this.entityValues.contractObservation = value;
  }

  private get documentationhasChanges() {
    return (
      this.entityValues.showDocumentation?.some(
        (item: any) =>
          item.__status__ !== "original" && item.__status__ !== "empty"
      ) ?? false
    );
  }

  private get associatedDocumentshasChanges() {
    return (
      this.entityValues.showAssociatedDocuments?.some(
        (item: any) =>
          item.__status__ !== "original" && item.__status__ !== "empty"
      ) ?? false
    );
  }

  private get userAttachmentshasChanges() {
    return (
      this.entityValues.showUserAttachments?.some(
        (item: any) =>
          item.__status__ !== "original" && item.__status__ !== "empty"
      ) ?? false
    );
  }

  userAttachmentsLoadRow(row: any) {
    this.userAttachmentsUpdateStore.loadValuesFromRow(row);
  }

  userAttachmentsRestore(id: string) {
    this.entityValues.showUserAttachments =
      this.entityValues.showUserAttachments.map((item) =>
        item.id === id
          ? { ...item, __status__: restoredStatus(item.__status__) }
          : item
      );
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  userAttachmentsValidateChanges(_changes: GridChangeSet) {
    const errors = this.userAttachmentsUpdateStore.validateData(
      this.userAttachmentsUpdateStore.entityValues
    );

    this.userAttachmentsUpdateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  userAttachmentsCommitInlineChange(
    rowId: string,
    changes: Record<string, any>
  ) {
    this.entityValues.showUserAttachments =
      this.entityValues.showUserAttachments.map((item) =>
        item.id === rowId
          ? { ...item, ...changes, __status__: updatedStatus(item.__status__) }
          : item
      );
  }

  userAttachmentsCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.userAttachmentsUpdateStore.entityValues;
      const hadChanges = this.userAttachmentsUpdateStore.pendingChanges;
      if (hadChanges) {
        const userAttachments = this.entityValues.showUserAttachments;
        for (
          let index = 0;
          index < this.entityValues.showUserAttachments.length;
          index++
        ) {
          if (userAttachments[index].id === values.id) {
            this.entityValues.showUserAttachments =
              this.entityValues.showUserAttachments.map((item, idx) =>
                idx === index
                  ? { ...values, __status__: updatedStatus(values.__status__) }
                  : item
              );
            break;
          }
        }
        this.userAttachmentsUpdateStore.clearFields();
      }
    } else if (changes.added) {
      const values = this.userAttachmentsUpdateStore.entityValues;
      const newId = `${this.uniqueCounter++}`;
      if (!this.entityValues.showUserAttachments) {
        this.entityValues.showUserAttachments = [];
      }
      this.entityValues.showUserAttachments = [
        ...this.entityValues.showUserAttachments,
        {
          ...values,
          id: newId,
          __status__: "added",
        },
      ];
      this.userAttachmentsUpdateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const userAttachments = this.entityValues.showUserAttachments;
      for (
        let index = 0;
        index < this.entityValues.showUserAttachments.length;
        index++
      ) {
        if (userAttachments[index].id === id) {
          if (isOriginalCrudStatus(userAttachments[index].__status__)) {
            this.entityValues.showUserAttachments =
              this.entityValues.showUserAttachments.map((item, idx) =>
                idx === index
                  ? { ...item, __status__: deleteStatus(item.__status__) }
                  : item
              );
          } else {
            this.entityValues.showUserAttachments =
              this.entityValues.showUserAttachments.filter((x) => x.id !== id);
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.userAttachments);
    //   this.setCRUDRowsFromValues(newChanges, this.userAttachmentsUpdateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.userAttachments = getNewCRUDRows(newChanges, this.entityValues.userAttachments, this.uniqueCounter++);
    // }
  }

  getCreatedAt_Value(): Date | null {
    return this.entityValues.createdAt_;
  }

  getLastUpdatedAt_Value(): Date | null {
    return this.entityValues.lastUpdatedAt_;
  }

  addNewEditionType(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Edition",
        fieldName: callerFieldName || "",
        isMulti: false,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/editionType/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewMusicalWork(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Edition",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/musicalWork/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.editionInstance = null;
    this.signatureStatuses = [];
    this.entityValues = EditionValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      editionWorkflow: { open: true, errors: [] },
      signatureStatus: { open: true, errors: [] },
      editionData: { open: true, errors: [] },
      contracts: { open: true, errors: [] },
      editionContracts: { open: true, errors: [] },
      editionDocumentation: { open: true, errors: [] },
      editionUserAttachments: { open: true, errors: [] },
      systemDates: { open: false, errors: [] },
    };
    this.editionTypeSearchValue = "";
    this.editionTypeOptions = [];
    this.worksSearchValue = "";
    this.worksOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: EditionValuesType): EditionErrorsType {
    const errors: EditionErrorsType = {};
    this.clearErrors();
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EditionNameLabel,
      };
      this.setCardError("editionData", errors.name.message);
    }
    this.editionTypeError = undefined;
    if (values.showEditionType === null || values.showEditionType.id === "") {
      errors.editionType = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EditionEditionTypeLabel,
      };
      this.setCardError("editionData", errors.editionType.message);
    }

    if (!values.contentType) {
      errors.contentType = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EditionContentTypeLabel,
      };
      this.setCardError("editionData", errors.contentType.message);
    }

    if (isNil(values.price)) {
      errors.price = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.EditionPriceLabel,
      };
      this.setCardError("editionData", errors.price.message);
    }
    if (values.showWorks.length === 0) {
      errors.works = {
        message: this.langStore.atLeastOneValue,
        fieldLabel: this.langStore.EditionWorksLabel,
      };
      this.setCardError("editionData", errors.works.message);
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "EditionUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    // Guardado pero desactualizado: cuenta lo escrito desde que se entró en ese
    // estado (la foto, y el vigía para lo que la foto no cubre), no las filas y
    // los archivos que ya se guardaron.
    if (this.savedButOutdated)
      return this._ownPendingChanges || this.editedWhileOutdated;
    return this._ownPendingChanges || this.hasArrayChanges();
  }

  private hasArrayChanges(): boolean {
    return (
      this.documentationhasChanges ||
      this.associatedDocumentshasChanges ||
      this.userAttachmentshasChanges
    );
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name: this.entityValues.name,
      contentType: this.entityValues.contentType,
      publicationLink: this.entityValues.publicationLink,
      price: this.entityValues.price,
      notes: this.entityValues.notes,
      contractObservation: this.entityValues.contractObservation,
      editionType: this.entityValues.showEditionType?.id || null,
      works: (this.entityValues.showWorks || []).map((x: any) => x.id).sort(),
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }
    if (this.documentationhasChanges) changed.push("documentation");
    if (this.associatedDocumentshasChanges) changed.push("associatedDocuments");
    if (this.userAttachmentshasChanges) changed.push("userAttachments");
    return changed;
  }

  *saveEdition(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados. Tampoco con el formulario
    // guardado pero desactualizado: no se guarda desde un estado viejo.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Mientras se guarda, la capa no deja editar. Si el formulario cambia igual
      // (algún camino programático), lo que se ve ya no es lo que se guardó, y no
      // se concilia nada: queda guardado pero desactualizado, como si la relectura
      // fallara. La versión nueva, que _save toma de la respuesta, no cuenta.
      let changedWhileSaving = false;
      const stopWatching = reaction(
        () => ({ ...toJS(this.entityValues), version_: undefined }),
        () => {
          changedWhileSaving = true;
        },
        { equals: (a, b) => _.isEqual(a, b) }
      );
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        } finally {
          stopWatching();
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            // El formulario queda con el registro del server, no con lo que mandó:
            // las filas nuevas toman su id, los archivos quedan guardados (no
            // "added", que se volverían a mandar) y version_ es el que quedó. Si el
            // formulario sigue abierto (volver atrás no navega, por ejemplo en una
            // pestaña nueva), el próximo guardado parte de ahí. Si no se puede poner
            // al día (cambió mientras se guardaba, o la relectura falla), el guardado
            // ya está hecho, pero lo que se ve no es lo que hay: queda guardado pero
            // desactualizado, con Guardar deshabilitado y el aviso con Recargar, y
            // no se cierra.
            if (changedWhileSaving) {
              this.becomeOutdated();
            } else {
              try {
                yield this.loadEditionById(this.savedId);
              } catch (error) {
                console.warn(
                  "[EditionUpdateStore] saved, but reloading the record failed:",
                  error
                );
                this.becomeOutdated();
              }
            }
            if (!this.savedButOutdated) {
              this.resetPendingChanges();
              this.saveOriginalState();
            }
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          if (!this.savedButOutdated)
            this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelEdition(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getEditionValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = EditionValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    if (this.editionInstance) {
      const variables = getEditionUpdateVariables(this.entityValues);
      this.savedId = null;
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.editionUpdate({
        ...variables,
        idempotencyKey_: this.idempotencyKeyFor(variables),
      });
      this.savedId = result.editionUpdate.id;
      // La versión nueva la da el server: si el formulario sigue abierto (volver
      // atrás no navega, por ejemplo en una pestaña nueva), el próximo guardado
      // la manda y no choca consigo mismo.
      this.entityValues.version_ = result.editionUpdate.version_;
      this.saveAttempt = null;
      this.editionInstance = result.editionUpdate as Edition;
    }
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      // Register custom validators

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadEditionById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      yield this.loadEditionTypeValues();
      yield this.loadWorksValues();
      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }

  // ========== Métodos manuales específicos de Edition ==========

  *downloadAssociatedDocument(documentId: string): any {
    const documentData = this.entityValues.showAssociatedDocuments.find((doc: any) => doc.id === documentId);

    if (!documentData) {
      console.error(`No se encontró documento con ID: ${documentId}`);
      return;
    }

    const files = (documentData as any).__file_files;

    if (!files || files.length === 0) {
      console.error(`No hay archivo disponible para: ${(documentData as any).name ?? documentId}`);
      return;
    }

    const fileData = files[0];

    yield downloadWithAuth({
      entityName: "AssociatedDocumentEdition",
      fieldName: "file",
      instanceId: documentId,
      fileName: fileData.name
    });
  }

  *downloadDocumentationPDF(documentId: string): any {
    const documentData = this.entityValues.showDocumentation.find((doc: any) => doc.id === documentId);

    if (!documentData) {
      console.error(`No se encontró documento con ID: ${documentId}`);
      return;
    }

    const pdfFiles = (documentData as any).__finalDocumentPdf_files;

    if (!pdfFiles || pdfFiles.length === 0) {
      console.error(`No hay PDF disponible para el documento: ${(documentData as any).contractNumber ?? documentId}`);
      return;
    }

    const pdfData = pdfFiles[0];

    yield downloadWithAuth({
      entityName: "EditionDocumentation",
      fieldName: "finalDocumentPdf",
      instanceId: documentId,
      fileName: pdfData.name
    });
  }

  /**
   * Ejemplar de edición pre-generado al cierre de contratos (Edition.ejemplarPdf,
   * .fillingOnServer): filas para ShowFilesCompact desde entityValues.
   */
  get ejemplarPdfFiles(): CrudRows {
    const files = (this.entityValues as any).__ejemplarPdf_files || [];
    const rows: CrudRows = {};
    for (const f of files) {
      rows[f.id] = { ...f };
    }
    return rows;
  }

  *downloadEjemplarPdf(props: any): any {
    yield downloadWithAuth({
      entityName: "Edition",
      fieldName: "ejemplarPdf",
      instanceId: this.entityValues.id!,
      fileName: props.name,
    });
  }

  *viewEjemplarPdf(props: any): any {
    try {
      this.pdfViewerLoading = true;
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000/graphql";
      const serverUrl = apiUrl.replace(/\/graphql$/, "");
      const downloadUrl = `/Edition.ejemplarPdf/${this.entityValues.id}/${encodeURIComponent(props.name)}`;

      const token = yield tokenManager.getValidAccessToken();
      const response = yield fetch(`${serverUrl}/download${downloadUrl}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = yield response.blob();
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    } finally {
      this.pdfViewerLoading = false;
    }
  }

  openPdfViewer(url: string, title: string, warning?: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerWarning = warning || "";
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
    this.pdfViewerWarning = "";
  }

  *viewDocumentationPDF(documentId: string): any {
    const documentData = this.entityValues.showDocumentation.find((doc: any) => doc.id === documentId);

    if (!documentData) {
      console.error(`No se encontró documento con ID: ${documentId}`);
      return;
    }

    const pdfFiles = (documentData as any).__finalDocumentPdf_files;

    if (!pdfFiles || pdfFiles.length === 0) {
      console.error(`No hay PDF disponible para el documento: ${(documentData as any).contractNumber}`);
      return;
    }

    const pdfData = pdfFiles[0];

    try {
      this.pdfViewerLoading = true;

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000/graphql";
      const serverUrl = apiUrl.replace(/\/graphql$/, "");
      const downloadUrl = `/EditionDocumentation.finalDocumentPdf/${documentId}/${encodeURIComponent(pdfData.name)}`;

      const token = yield tokenManager.getValidAccessToken();
      const response = yield fetch(`${serverUrl}/download${downloadUrl}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = yield response.blob();

      const blobUrl = window.URL.createObjectURL(blob);
      this.openPdfViewer(blobUrl, pdfData.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    } finally {
      this.pdfViewerLoading = false;
    }
  }

  *viewAssociatedDocumentPDF(documentId: string): any {
    const documentData = this.entityValues.showAssociatedDocuments.find((doc: any) => doc.id === documentId);

    if (!documentData) {
      console.error(`No se encontró documento adjunto con ID: ${documentId}`);
      return;
    }

    const files = (documentData as any).__file_files;

    if (!files || files.length === 0) {
      console.error(`No hay archivo disponible para el documento: ${documentId}`);
      return;
    }

    const fileData = files[0];

    if (!fileData.name.toLowerCase().endsWith('.pdf')) {
      console.error(`El archivo no es PDF: ${fileData.name}`);
      return;
    }

    try {
      this.pdfViewerLoading = true;

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000/graphql";
      const serverUrl = apiUrl.replace(/\/graphql$/, "");
      const downloadUrl = `/AssociatedDocumentEdition.file/${documentId}/${encodeURIComponent(fileData.name)}`;

      const token = yield tokenManager.getValidAccessToken();
      const response = yield fetch(`${serverUrl}/download${downloadUrl}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = yield response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      this.openPdfViewer(blobUrl, fileData.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    } finally {
      this.pdfViewerLoading = false;
    }
  }

  get worksWithAuthors(): any[] {
    const selectedIds = this.entityValues.showWorks.map(w => w.id);
    if (selectedIds.length === 0) {
      return [];
    }

    return selectedIds
      .map(id => {
        const fromInstance = this.editionInstance?.works?.find(w => w.id === id);
        if (fromInstance) return fromInstance;

        const fromOptions = this.worksOptions.find(w => w.id === id);
        if (fromOptions && fromOptions.showAuthors) {
          return {
            ...fromOptions,
            authors: fromOptions.showAuthors.map(a => ({
              id: a.id,
              author: {
                id: a.showAuthor?.id || '',
                displayId: a.showAuthor?.displayId || '',
                firstName: a.showAuthor?.displayId?.split(' ')[0] || '',
                lastName: a.showAuthor?.displayId?.split(' ').slice(1).join(' ') || ''
              },
              lyricsPercentage: a.lyricsPercentage,
              musicPercentage: a.musicPercentage
            }))
          };
        }
        return null;
      })
      .filter(work => work && (work as any).authors);
  }

  get hasDocumentationFiles(): boolean {
    const hasDocumentation = this.entityValues.showDocumentation?.some(
      (doc: any) => {
        const hasFiles = doc.__finalDocumentPdf_files && doc.__finalDocumentPdf_files.length > 0;
        const hasFileName = doc.finalDocumentPdf && doc.finalDocumentPdf.trim() !== '';
        return hasFiles || hasFileName;
      }
    ) ?? false;

    const hasAssociated = this.entityValues.showAssociatedDocuments?.some(
      (doc: any) => {
        const hasFiles = doc.__file_files && doc.__file_files.length > 0;
        const hasFileName = doc.file && doc.file.trim() !== '';
        return hasFiles || hasFileName;
      }
    ) ?? false;

    return hasDocumentation || hasAssociated;
  }

  async downloadAllFiles(): Promise<void> {
    const files: FileToDownload[] = [];

    for (const doc of this.entityValues.showDocumentation || []) {
      const pdfFiles = (doc as any).__finalDocumentPdf_files;
      if (pdfFiles && pdfFiles.length > 0) {
        files.push({
          entityName: "EditionDocumentation",
          fieldName: "finalDocumentPdf",
          instanceId: doc.id,
          fileName: pdfFiles[0].name,
          category: "Contratos"
        });
      }
    }

    for (const doc of this.entityValues.showAssociatedDocuments || []) {
      const fileList = (doc as any).__file_files;
      if (fileList && fileList.length > 0) {
        files.push({
          entityName: "AssociatedDocumentEdition",
          fieldName: "file",
          instanceId: doc.id,
          fileName: fileList[0].name,
          category: "Boletines"
        });
      }
    }

    const editionName = this.entityValues.name || "Edicion";

    const result = await this.rootStore.fileDownloadService.download({
      downloadName: editionName,
      files
    });

    if (result.failed.length > 0) {
      console.error("Archivos fallidos:", result.failed);
      alert(
        `Descarga completada con errores:\n` +
        `Exitosos: ${result.successful}/${result.total}\n` +
        `Fallidos: ${result.failed.length}\n\n` +
        result.failed.map(f => `- ${f.fileName} (${f.category}): ${f.error}`).join('\n')
      );
    }
  }
}
